import { useContext, useState } from "react";
import { X, AlertTriangle } from "lucide-react";
import { ProgramContext } from "../../context/ProgramsContext";
import toast from "react-hot-toast";

const CopyDayModal = () => {
    const {
        plan,
        setPlan,
        showCopyModal,
        setShowCopyModal,
        generateUniqueId,
        ACTIVITY_TYPES,
    } = useContext(ProgramContext);
    const newDate = new Date(showCopyModal?.date);
    const [searchTerm, setSearchTerm] = useState("");
    const [confirmCopy, setConfirmCopy] = useState(null); // holds sourceDay for confirmation

    const performCopy = (sourceDay) => {
        console.log("sourceDay.activities", sourceDay.activities)
        const updatedWeeks = plan.weeks.map((week) => {
            const updatedDays = week.days.map((day) => {
                if (day.id === showCopyModal.id) {
                    return {
                        ...day,
                        activities: JSON.parse(
                            JSON.stringify(
                                sourceDay.activities?.map((act) => ({
                                    ...act,
                                    is_complete: 0,
                                    is_skipped: 0,
                                    is_incomplete: 0,
                                    suggested_date: newDate.toISOString(),
                                    moved_date: newDate.toISOString(),
                                    id: generateUniqueId(),
                                    state: "SCHEDULED",
                                    activities: act?.activities?.map((e) => ({...e, is_complete: 0, is_skipped: 0}))
                                }))
                            )
                        ),
                    };
                }
                return day;
            });
            return { ...week, days: updatedDays };
        });

        const updatedPlan = { ...plan, weeks: updatedWeeks };
        setPlan(updatedPlan);
        toast.success("Activities copied successfully!");
        setShowCopyModal(null);
        setConfirmCopy(null);
        console.log("✅ Updated Plan:", updatedPlan);
    };

    const handleCopyClick = (sourceDay) => {
        const targetDay = allDays.find((d) => d.id === showCopyModal.id);
        if (targetDay?.activities?.length > 0) {
            setConfirmCopy(sourceDay); // show confirmation dialog
        } else {
            performCopy(sourceDay);
        }
    };

    const allDays = plan?.weeks?.flatMap((week) => week.days) || [];
    const copyToDayId = showCopyModal?.id;

    const filteredDays = allDays.filter((day) => {
        if (day.id === copyToDayId || day.activities.length === 0) return false;
        const dateObj = new Date(day.date);
        const weekday = dateObj.toLocaleDateString("en-US", { weekday: "short" }).toLowerCase();
        const dateLabel = dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric" }).toLowerCase();
        const keyword = searchTerm.toLowerCase();
        return weekday.includes(keyword) || dateLabel.includes(keyword);
    });

    const typeBgClass = {
        PT_SESSION: "bg-[#166273]/10 hover:bg-[#166273]/20",
        ROM_TEST: "bg-blue-100 hover:bg-blue-200",
        EDUCATION: "bg-yellow-100 hover:bg-yellow-200",
        QUESTIONNAIRE: "bg-purple-100 hover:bg-purple-200",
    };

    function isAllDaysEmpty(allDays) {
        return allDays.every(day => Array.isArray(day.activities) && day.activities.length === 0);
    }


    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60] animate-fade-in-up">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] sm:max-w-6xl p-6 transform transition-all flex flex-col max-h-[90vh] relative overflow-hidden">

                {/* Header */}
                <div className="flex justify-between items-start mb-4 border-b pb-4">
                    <div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                            Copy activities to{" "}
                            <span className="text-[#166273]">
                                {newDate.toLocaleDateString("en-US", {
                                    weekday: "short",
                                    month: "short",
                                    day: "numeric",
                                })}
                            </span>
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Select a day below to copy its activities into this one.
                        </p>
                    </div>
                    <button
                        onClick={() => setShowCopyModal(null)}
                        className="p-2 rounded-full hover:bg-gray-100 transition"
                        title="Close"
                        aria-label="Close copy modal"
                    >
                        <X size={22} className="text-gray-500" />
                    </button>
                </div>
                {isAllDaysEmpty(allDays) ?
                    <p className="text-gray-500 text-center col-span-full py-4">
                        No days to copy acitivity
                    </p>
                    :
                    <>
                        {/* Search */}
                        <div className="mb-4">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search by weekday or date (e.g. Mon, Jul 2)"
                                className="w-full px-4 py-2 border rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#166273]"
                            />
                        </div>

                        {/* Day Cards */}
                        <div className="overflow-y-auto flex-grow grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 py-4 px-2">
                            {filteredDays.length === 0 ? (
                                <p className="text-gray-500 text-center col-span-full">
                                    No matching days found.
                                </p>
                            ) : (
                                filteredDays.map((day) => {
                                    const dateObj = new Date(day.date);
                                    const weekday = dateObj.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
                                    const dateLabel = dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric" });

                                    return (
                                        <div key={day.id} className="bg-[#F9FAFB] rounded-xl shadow-lg p-4 flex flex-col justify-between">
                                            <div>
                                                <div className="flex justify-between items-center mb-2">
                                                    <h3 className="text-lg font-bold text-gray-800">{weekday}</h3>
                                                    <span className="text-sm text-gray-500">{dateLabel}</span>
                                                </div>
                                                <div className="space-y-2">
                                                    {day.activities.map((activity) => {
                                                        const typeClass = typeBgClass[activity.type] || "bg-gray-100 hover:bg-gray-200";
                                                        const typeInfo = ACTIVITY_TYPES[activity.type];
                                                        const detailsText = () => {
                                                            if (activity.type === "PT_SESSION") return `${activity.estTime} min / ${activity.exerciseCount} exercises`;
                                                            if (activity.type === "ROM_TEST") return `${activity.exercises?.length || 0} exercises`;
                                                            if (activity.estTime) return `Est. ${activity.estTime} min`;
                                                            if (activity.readTime) return `Read: ${activity.readTime} min`;
                                                            if (activity.videoLength) return `Video: ${activity.videoLength} min`;
                                                            return typeInfo?.name;
                                                        };
                                                        return (
                                                            <div key={activity.task_id || activity.id} className={`rounded-xl px-3 py-2 ${typeClass} pointer-events-none`}>
                                                                <p className="font-bold text-sm text-gray-800 leading-tight">{activity.title}</p>
                                                                <p className="text-xs text-gray-600 pt-1">{detailsText()}</p>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleCopyClick(day)}
                                                className="mt-4 px-4 py-2 bg-[#166273] hover:bg-[#166273]/90 text-white rounded transition"
                                            >
                                                Copy Day
                                            </button>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </>}

                {/* Confirmation Dialog */}
                {confirmCopy && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-50">
                        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-[90vw] sm:max-w-md">

                            <div className="text-center">
                                <AlertTriangle className="mx-auto h-12 w-12 text-yellow-500" />
                                <h3 className="mt-2 text-xl font-bold text-gray-800">Replace existing activities?</h3>
                                <p className="mt-2 text-sm text-gray-600">
                                    The selected day already has activities. Copying will replace all of them.
                                </p>
                            </div>

                            <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
                                <button
                                    onClick={() => setConfirmCopy(null)}
                                    className="px-4 py-2 text-gray-700 font-semibold rounded-lg border border-gray-300 hover:bg-gray-100 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => performCopy(confirmCopy)}
                                    className="px-4 py-2 bg-[#166273] text-white font-semibold rounded-lg shadow-md hover:bg-[#166273]/90 transition"
                                >
                                    Confirm
                                </button>
                            </div>

                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CopyDayModal;
